import { useApi } from '@hooks/useApi';
import { Blockquote } from '@ui';
import { Booking } from 'components/EventCalendar/types';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '../../Form/base/Form';
import { FormInput, FormSubmit } from '../../Form/next';

export type BookingRequestResponseAction = 'accept' | 'decline';

interface Props {
    action: BookingRequestResponseAction;
    booking: Booking;
    /** True when accepting will send a payment link instead of confirming right away. */
    withPayment?: boolean;
    callback?: () => void;
}

const BookingRequestResponseForm = ({ action, booking, withPayment, callback }: Props) => {
    const { t } = useTranslation('bookings');
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);

    // sends the decision to the backend and refreshes the list on success
    const submitHandler = (data) =>
        api({
            url: `booking/${booking.id}/${action}`,
            method: 'POST',
            data: action === 'decline' ? data : {},
            onBefore: () => setIsLoading(true),
            onSuccess: () => callback?.(),
            onError: () => setIsLoading(false)
        });

    return (
        <Form onSubmit={submitHandler}>
            <Blockquote>
                {t('booking.requestResponse.summary', {
                    name: booking.user_name,
                    email: booking.user_email,
                    event: booking.event?.title ?? '',
                    date: booking.event?.start ? moment(booking.event.start).format('DD.MM.YYYY') : '-'
                })}
            </Blockquote>

            {action === 'accept' ? (
                <p className="tw-my-4">
                    {withPayment
                        ? t('booking.requestResponse.acceptHintWithPayment')
                        : t('booking.requestResponse.acceptHint')}
                </p>
            ) : (
                <FormInput.Textarea name="reason" label={t('booking.requestResponse.reasonLabel')} />
            )}

            <FormSubmit isLoading={isLoading} color={action === 'accept' ? 'primary' : 'error'}>
                {action === 'accept'
                    ? t('booking.requestResponse.acceptButton')
                    : t('booking.requestResponse.declineButton')}
            </FormSubmit>
        </Form>
    );
};

export default BookingRequestResponseForm;
